/**
 * **목록의 한 줄에서 창을 열고, 창 안에서 줄을 골라, 창의 단추로 확정한다.**
 *
 * 예금관리(`/w_tb_ca642_ats`)의 입금처리가 이 모양이다. 운영 사용 기록 6일치(2026-09-29~10-04)에서
 * 쓰기 확정 3,139건 중 352건(11%, 테넌트 6곳)이었다. 사람은
 *
 * 1. 은행 연동이 넣은 입출금 줄의 처리 체크(`mismijchk`)를 켠다 → 그 거래처의 미수 창이 뜬다
 * 2. 창에서 미수를 **하나** 고른다(210건 중 195건) — 화면이 입금액을 그 미수에 나눠 넣는다
 * 3. 창의 `확인` → `입금처리하시겠습니까?` → `저장: 서버에 잘 저장했습니다`
 *
 * 준비(`prepareWindowPick`)는 1·2 까지 하고 멈춘다. 3 은 저장과 같은 Auto 게이트에서 확정
 * (`applyWindowPick`)이 한다. 꺼져 있으면 사람은 창에서 `확인` 하나만 누르면 된다.
 *
 * **처리 체크를 켜는 것은 쓰기가 아니다**(2026-10-04 개발 테넌트). 체크하면 화면이 서버의
 * `dw_list_itemchanged` 를 부르지만, 창을 그냥 닫고 다시 조회하면 그 줄은 `compflag 0`,
 * `mismijchk ''` 그대로였고 미수 잔액도 그대로였다. 반대로 **이미 처리된 줄**의 체크를 누르면
 * `이미 입출금 처리 되었습니다. 취소하시겠습니까?` 가 뜨고, 예를 누르면 그 처리가 지워진다 —
 * 그래서 켜진 줄은 누르지 않는다.
 *
 * 무엇이 화면의 사정인지(어느 칸이 창을 여는지, 어느 단추가 확정인지, 무엇을 고르는지)는
 * 스킬이 정해 보낸다. 여기서는 그 일을 사람과 같은 순서로 할 뿐이다.
 */
import { bridge } from './bridge';
import { locateCell } from './grid';
import { OptionNotFound } from './input';
import { sameName } from './popup';
import { onScreenAll } from './visible';
import {
  cancelQuestion, confirmQuestion, dismissNotices, questionAsked, settleDialog,
} from './dialogs';

export type WindowPickSpec = {
  /** 목록 줄에서 누르면 창이 뜨는 체크 칸. **이미 켜져 있으면 끝난 줄이다.** */
  open: string;
  /** 창 안 표에서 고르는 체크 칸. */
  check: string;
  /** 창의 확정 단추 글자(`확인`). */
  button: string;
  /** 사람에게 부를 일 이름(`입금처리`). */
  action: string;
  /** 확정 뒤 다시 조회된 목록에서 같은 줄을 찾을 칸들(`snddate`, `sndnum`). */
  identity: string[];
  /**
   * 창 안에서 고른 줄을 다시 찾을 칸들(`misdate`, `misnum`). 창은 고른 뒤 줄을 다시 늘어놓고,
   * 행을 앱의 모델로 갈아 끼우며 모델에 없는 칸을 버린다 — 아무 날짜·번호 칸으로 찾으면 놓친다.
   */
  windowIdentity: string[];
  /** 이 일로 다루는 줄만 — 칸 → 허용 값. 밖의 줄은 누르지 않는다. */
  only?: Record<string, string[]>;
  /** `only` 밖의 줄을 말했을 때 사람에게 할 말. */
  onlyReason?: string;
  /**
   * 사람이 창의 줄을 말하지 않았을 때 고를 줄. **잔액(`balance`)이 그 줄의 금액(`amount` 의 합)과
   * 같은 것**, 없으면 **가장 오래된 것**(`order` 오름차순).
   */
  pick?: {
    amount: string[]; balance: string; order: string;
    /** 고른 줄에 화면이 나눠 넣는 금액 칸(`winamt`). 이것이 0 보다 커야 고른 것이다. */
    paid?: string;
  };
  /** 사람에게 보일 목록 줄 이름에 쓰는 칸들. */
  rowLabel?: string[];
  /** 사람에게 보일 창 줄 이름에 쓰는 칸들. */
  windowLabel?: string[];
};

export type WindowPickRequest = {
  routePath: string;
  rowsStateKey: string;
  /** 창 안 표. */
  cellsStateKey: string;
  match: Record<string, string>;
  /** 사람이 말한 창 안 줄. 없으면 `spec.pick` 규칙으로 고른다. */
  cellsMatch?: Record<string, string>;
  /** 되묻기에 답한 글자. 목록 줄 이름일 수도, 창 줄 이름일 수도 있다. */
  pick?: string;
  spec: WindowPickSpec;
};

/** 준비가 짚어 둔 것. 확정이 한 번만 쓴다. */
export type WindowPending = {
  route: string;
  rowsStateKey: string;
  cellsStateKey: string;
  spec: WindowPickSpec;
  /** 목록에서 같은 줄을 다시 찾을 값. */
  identity: Record<string, string>;
  /** 창에서 고른 줄을 다시 찾을 값. */
  picked: Record<string, string>;
};

const POLL_MS = 25;
/** 창이 뜨기까지 — 서버를 두 번 다녀온다(체크 반영, 미수 조회). */
const OPEN_MS = 15000;
/** 고른 뒤 화면이 금액을 나눠 넣기까지. 앱은 100ms 뒤에 나눈다(`setTimeout(itemchanged, 100)`). */
const SPREAD_MS = 4000;
/** 이만큼 조용하면 멎은 것으로 본다. */
const QUIET_MS = 600;

type Row = Record<string, unknown>;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

function rowsOf(stateKey: string): Row[] {
  const rows = bridge().pageState()?.[stateKey];
  return Array.isArray(rows) ? rows as Row[] : [];
}

function text(value: unknown): string {
  return String(value ?? '').trim();
}

function amountOf(value: unknown): number {
  const number = Number(text(value).replace(/[,원\s]/g, ''));
  return Number.isFinite(number) ? number : 0;
}

/** 숫자처럼 생긴 말인가. `10000`, `10,000`, `10,000원`. */
function numeric(value: string): boolean {
  return /^-?[\d,]+(원)?$/.test(value.trim()) && /\d/.test(value);
}

/** `20261004` → `2026-10-04`. 여덟 자리 날짜가 아니면 그대로. */
function dashed(value: string): string {
  return /^\d{8}$/.test(value) ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}` : value;
}

function won(value: number): string {
  return `${value.toLocaleString('ko-KR')}원`;
}

function clean(value: string): string {
  return value.replace(/[^0-9A-Za-z가-힣]/g, '').toLocaleLowerCase();
}

/** 그 표가 화면에 떠 있는가. 창이 닫히면 표가 DOM 에서 빠진다(`isVisible && …`). */
function gridShown(stateKey: string): boolean {
  return onScreenAll(`[data-el-grid="${stateKey}"]`).length > 0;
}

async function untilQuiet(stateKey: string, deadline: number): Promise<void> {
  let calm = 0;
  let last = bridge().pageState()?.[stateKey];
  while (calm < QUIET_MS && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
    const now = bridge().pageState()?.[stateKey];
    if ((bridge().busy?.() ?? false) || now !== last) {
      calm = 0;
      last = now;
    } else {
      calm += POLL_MS;
    }
  }
}

/**
 * 그 표를 품은 창에서 **가장 가까운** 그 글자의 단추.
 *
 * 창을 `[role=dialog]` 로 찾지 않는다 — 그 표식은 기록기 브랜치에만 있다(2026-09-30).
 * 표에서 조상으로 올라가며 찾는다. 예금관리에는 `popupList` 표를 쓰는 창이 둘(입금·지급)이고
 * 하나만 떠 있다 — 보이는 표에서 시작한다.
 */
export function windowButton(stateKey: string, label: string): HTMLElement | undefined {
  const grid = onScreenAll<HTMLElement>(`[data-el-grid="${stateKey}"]`)[0];
  let scope: HTMLElement | null = grid?.parentElement ?? null;
  for (let step = 0; step < 10 && scope; step += 1) {
    const found = Array.from(scope.querySelectorAll<HTMLElement>('button'))
      .find((one) => (one.textContent ?? '').replace(/\s+/g, '') === label.replace(/\s+/g, ''));
    if (found) return found;
    scope = scope.parentElement;
  }
  return undefined;
}

/** 체크 칸의 위젯. 이 앱의 체크박스는 입력 요소가 아니라 스스로 `role="check-box"` 라 밝힌다. */
function checkWidget(cell: HTMLElement): HTMLElement | null {
  return cell.querySelector<HTMLElement>('[role="check-box"]')
    ?? cell.querySelector<HTMLElement>('input[type="checkbox"]')
    ?? cell.querySelector<HTMLElement>('[class*=CheckBox]');
}

function press(element: HTMLElement): void {
  for (const type of ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click']) {
    element.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
  }
}

/**
 * 말한 값이 그 칸에 맞는가. 단계(`tier`)가 오를수록 느슨하다.
 *
 * - 금액은 수로 견준다(`10000` = `10,000`).
 * - 날짜(`…date`)는 숫자만 견준다(`2026-10-04` = `20261004`).
 * - 이름은 글자가 같은 것 → 앞이 같은 것(`엘엔티티` = `엘엔티티m`) → 가운데 든 것.
 *   이름 칸이 빈 줄은 **적요**에서 찾는다 — 은행 연동 줄은 거래처가 안 붙은 채 들어오고,
 *   입금자 이름은 적요에 있다.
 */
function fits(row: Row, key: string, wanted: string, tier: number): boolean {
  const want = wanted.trim();
  if (!want) return true;
  const value = text(row[key]);
  if (/date$/i.test(key)) {
    const digits = want.replace(/\D/g, '');
    return digits.length >= 4 && value.replace(/\D/g, '').startsWith(digits);
  }
  if (numeric(want)) return amountOf(value) === amountOf(want) && value !== '';
  const named = (candidate: string): boolean => {
    if (!candidate) return false;
    if (tier === 0) return candidate === want;
    if (tier === 1) return sameName(candidate, want);
    const needle = clean(want);
    return needle.length >= 2 && clean(candidate).includes(needle);
  };
  if (named(value)) return true;
  return tier === 2 && !value && /nm$/i.test(key) && named(text(row.remark));
}

/** 말한 것들에 맞는 줄. 엄한 단계부터 넓혀 처음 걸리는 단계의 줄들이다. */
function matching(rows: Row[], match: Record<string, string>): Row[] {
  const keys = Object.keys(match).filter((key) => text(match[key]));
  if (!keys.length) return rows;
  for (let tier = 0; tier < 3; tier += 1) {
    const found = rows.filter((row) => keys.every((key) => fits(row, key, match[key] ?? '', tier)));
    if (found.length) return found;
  }
  return [];
}

function rowName(row: Row, spec: WindowPickSpec): string {
  const parts = (spec.rowLabel ?? []).map((key) => dashed(text(row[key]))).filter(Boolean);
  const amount = (spec.pick?.amount ?? []).reduce((sum, key) => sum + amountOf(row[key]), 0);
  if (spec.pick?.amount?.length) parts.push(won(amount));
  return Array.from(new Set(parts)).join(' ');
}

function windowName(row: Row, spec: WindowPickSpec): string {
  const parts = (spec.windowLabel ?? []).map((key) => dashed(text(row[key]))).filter(Boolean);
  if (spec.pick?.balance) parts.push(`잔액 ${won(amountOf(row[spec.pick.balance]))}`);
  return Array.from(new Set(parts)).join(' ');
}

/**
 * 줄마다 사람에게 보일 이름. 같은 이름이 둘이면 마지막 식별 칸(번호)을 붙여 가른다 —
 * 같은 날 같은 거래처가 같은 금액을 두 번 보낼 수 있다.
 */
function names(rows: Row[], nameOf: (row: Row) => string, tail: string[]): Map<string, Row> {
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(nameOf(row), (counts.get(nameOf(row)) ?? 0) + 1);
  const out = new Map<string, Row>();
  for (const row of rows) {
    const base = nameOf(row);
    const extra = tail.map((key) => text(row[key])).filter(Boolean).join('-');
    const name = (counts.get(base) ?? 0) > 1 && extra ? `${base} (${extra})` : base;
    if (!out.has(name)) out.set(name, row);
  }
  return out;
}

/** 그 줄을 다시 찾을 값. 줄 순서는 믿지 않는다. */
function keyOf(row: Row, keys: string[]): Record<string, string> {
  return Object.fromEntries(keys
    .map((key) => [key, text(row[key])])
    .filter(([, value]) => value));
}

function same(row: Row, key: Record<string, string>): boolean {
  return Object.entries(key).every(([name, value]) => text(row[name]) === value);
}

/**
 * 창에서 고를 줄. 사람이 말한 것이 있으면 그것(여럿이면 그 안에서 규칙대로),
 * 없으면 규칙대로 — **잔액이 금액과 같은 것, 없으면 가장 오래된 것.**
 *
 * 규칙은 사람이 한 일에서 왔다(기록기 v2, 2026-10-01~02 입금처리 210건): 미수를 하나만 고른
 * 195건 중 창의 첫 줄(가장 오래된 것)이 150건, 다른 줄이 59건. 다른 줄은 잔액이 입금액과 같은
 * 미수로 보인다(미확인 — 기록기가 금액을 남기지 않는다).
 */
function chooseInWindow(
  rows: Row[],
  amount: number,
  spec: WindowPickSpec,
  said?: Record<string, string>,
): Row | undefined {
  const balance = spec.pick?.balance;
  const order = spec.pick?.order;
  // 잔액이 없는 줄은 고를 수 없다 — 화면이 그 체크를 잠근다(`readonly={misamt < 1}`).
  const open = balance ? rows.filter((row) => amountOf(row[balance]) > 0) : rows;
  const pool = said && Object.keys(said).some((key) => text(said[key])) ? matching(open, said) : open;
  if (!pool.length) return undefined;
  const oldest = (list: Row[]): Row | undefined => (order
    ? list.slice().sort((a, b) => text(a[order]).localeCompare(text(b[order])))[0]
    : list[0]);
  if (balance && amount > 0) {
    const even = pool.filter((row) => amountOf(row[balance]) === amount);
    if (even.length) return oldest(even);
  }
  return oldest(pool);
}

/**
 * 창을 열고 줄을 고른다. **확정은 하지 않는다.**
 *
 * `loadList` 는 그 목록을 화면에 올리는 일(조건 넣고 조회), `closeWindow` 는 앞 판이 열어 둔
 * 창을 닫는 일 — 둘 다 `Session` 이 이미 갖고 있다.
 */
export async function prepareWindowPick(
  request: WindowPickRequest,
  loadList: () => Promise<void>,
  closeWindow: (stateKey: string) => Promise<void>,
  timeoutMs: number,
): Promise<{ pending: WindowPending; row: string; name: string; picked: string }> {
  const { spec } = request;
  // 앞 판이 열어 둔 창이 있으면 닫는다. 떠 있으면 새 창이 열렸는지 못 잰다.
  await closeWindow(request.cellsStateKey);
  await loadList();
  const rows = rowsOf(request.rowsStateKey);
  const inScope = (row: Row): boolean => Object.entries(spec.only ?? {})
    .every(([key, allowed]) => allowed.includes(text(row[key])));
  const done = (row: Row): boolean => text(row[spec.open]) === '1';
  const nameOf = (row: Row): string => rowName(row, spec);
  const tail = spec.identity.slice(-1);
  const wanted = Object.values(request.match).filter((value) => text(value)).join(' ');

  // 되묻기 답은 우리가 내민 글자 그대로다. 목록 줄 이름이면 그 줄이고, 아니면 창 줄 이름이다.
  let windowPick: string | undefined;
  let chosen: Row | undefined;
  if (request.pick?.trim()) {
    chosen = names(rows, nameOf, tail).get(request.pick.trim());
    if (!chosen) windowPick = request.pick.trim();
  }
  if (!chosen) {
    const hits = matching(rows, request.match);
    const ready = hits.filter((row) => inScope(row) && !done(row));
    const readyNames = names(ready, nameOf, tail);
    if (readyNames.size === 1) {
      chosen = Array.from(readyNames.values())[0];
    } else if (readyNames.size > 1) {
      throw new OptionNotFound(wanted, Array.from(readyNames.keys()).slice(0, 10));
    } else {
      const finished = hits.find((row) => inScope(row) && done(row));
      if (finished) {
        throw new Error(`${nameOf(finished)} 줄은 이미 ${spec.action}되어 있습니다. 아무것도 바꾸지 않았습니다.`);
      }
      const outside = hits.find((row) => !inScope(row));
      if (outside) {
        throw new Error(`${nameOf(outside)} 줄은 ${spec.action}하지 않았습니다.`
          + `${spec.onlyReason ? ` ${spec.onlyReason}` : ''}`);
      }
      const left = names(rows.filter((row) => inScope(row) && !done(row)), nameOf, tail);
      if (left.size) throw new OptionNotFound(wanted, Array.from(left.keys()).slice(0, 10));
      throw new Error(`${spec.action}할 줄이 목록에 없습니다. 언제 들어온 것인지 말씀해 주시면 그 기간을 찾아보겠습니다.`);
    }
  }
  if (!chosen) throw new Error(`${spec.action}할 줄을 찾지 못했습니다.`);
  const label = nameOf(chosen);
  if (done(chosen)) {
    // 켜진 체크를 누르면 화면은 처리를 지우겠느냐고 묻는다. 누르지 않는다.
    throw new Error(`${label} 줄은 이미 ${spec.action}되어 있습니다. 아무것도 바꾸지 않았습니다.`);
  }
  if (!inScope(chosen)) {
    throw new Error(`${label} 줄은 ${spec.action}하지 않았습니다.${spec.onlyReason ? ` ${spec.onlyReason}` : ''}`);
  }
  const identity = keyOf(chosen, spec.identity);
  const amount = (spec.pick?.amount ?? []).reduce((sum, key) => sum + amountOf(chosen![key]), 0);

  // 1. 처리 체크를 켠다 — 창이 뜬다.
  const index = rowsOf(request.rowsStateKey).findIndex((row) => same(row, identity));
  const opener = index >= 0 ? await locateCell(request.rowsStateKey, index, spec.open) : null;
  const widget = opener && checkWidget(opener);
  if (!widget) throw new Error(`${label} 줄의 체크 칸을 화면에서 찾지 못했습니다. 아무것도 바꾸지 않았습니다.`);
  const before = bridge().pageState()?.[request.cellsStateKey];
  // 앞서 떠 있던 알림은 이 누름의 결과가 아니다.
  await dismissNotices();
  press(widget);
  const deadline = Date.now() + Math.max(timeoutMs, OPEN_MS);
  const said: string[] = [];
  while (Date.now() < deadline) {
    // **묻는 창이 뜨면 아니오다.** 켜진 줄을 누르면 처리를 지우겠느냐고 묻는다 — 위에서 걸렀지만
    // 화면이 우리가 읽은 것과 다르게 알고 있으면 여기로 온다.
    const asked = questionAsked();
    if (asked) {
      cancelQuestion();
      // eslint-disable-next-line no-await-in-loop
      await settleDialog(1000);
      throw new Error(`${label} 줄을 누르자 화면이 물었습니다: ${asked} — 아니오로 답했습니다. 아무것도 바꾸지 않았습니다.`);
    }
    // eslint-disable-next-line no-await-in-loop
    said.push(...await dismissNotices(3, 0));
    const fresh = bridge().pageState()?.[request.cellsStateKey] !== before;
    if (gridShown(request.cellsStateKey) && fresh && !(bridge().busy?.() ?? false)) break;
    // 창이 안 뜨고 화면이 무언가 말했으면 그것이 이유다(`은행을 선택하세요`).
    if (said.length && !gridShown(request.cellsStateKey) && !(bridge().busy?.() ?? false)) break;
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS * 4);
  }
  if (!gridShown(request.cellsStateKey)) {
    throw new Error(`${label} 줄의 ${spec.action} 창이 뜨지 않았습니다.`
      + `${said.length ? ` 화면 안내: ${said.join(' / ')}` : ''} 아무것도 바꾸지 않았습니다.`);
  }
  await untilQuiet(request.cellsStateKey, Date.now() + timeoutMs);

  // 2. 창에서 줄을 고른다.
  const inside = rowsOf(request.cellsStateKey);
  const insideNames = names(inside, (row) => windowName(row, spec), []);
  let target: Row | undefined = windowPick ? insideNames.get(windowPick) : undefined;
  if (!target) target = chooseInWindow(inside, amount, spec, request.cellsMatch);
  if (!target) {
    const choices = Array.from(insideNames.keys()).slice(0, 10);
    await closeWindow(request.cellsStateKey);
    if (!inside.length) throw new Error(`${label} 줄의 ${spec.action} 창에 고를 것이 없습니다. 아무것도 바꾸지 않았습니다.`);
    throw new OptionNotFound(Object.values(request.cellsMatch ?? {}).join(' '), choices);
  }
  const picked = keyOf(target, spec.windowIdentity);
  const pickedName = windowName(target, spec);
  const at = (): number => rowsOf(request.cellsStateKey).findIndex((row) => same(row, picked));
  const cell = await locateCell(request.cellsStateKey, at(), spec.check);
  const box = cell && checkWidget(cell);
  if (!box) {
    await closeWindow(request.cellsStateKey);
    throw new Error(`${pickedName} 줄의 체크 칸을 창에서 찾지 못했습니다. 아무것도 바꾸지 않았습니다.`);
  }
  const shown = rowsOf(request.cellsStateKey)[at()] ?? {};
  const untouched = JSON.stringify({ ...shown, [spec.check]: '' });
  press(box);
  // **체크만 켜진 것으로는 모자란다.** 화면이 그 줄에 금액을 나눠 넣어야 확정이 옳은 금액을
  // 보낸다. 체크 값만 바꾸는 좁은 통로(`setRowCheck`)는 그 나눔을 건너뛰므로 여기서는 안 쓴다.
  const spreadBy = Date.now() + SPREAD_MS;
  const paid = spec.pick?.paid;
  const spread = (): boolean => {
    const now = rowsOf(request.cellsStateKey)[at()];
    if (!now || text(now[spec.check]) !== '1') return false;
    return paid ? amountOf(now[paid]) > 0 : JSON.stringify({ ...now, [spec.check]: '' }) !== untouched;
  };
  const warned: string[] = [];
  while (!spread() && Date.now() < spreadBy) {
    // eslint-disable-next-line no-await-in-loop
    warned.push(...await dismissNotices(3, 0));
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS * 2);
  }
  await untilQuiet(request.cellsStateKey, Date.now() + timeoutMs);
  warned.push(...await dismissNotices(3, 0));
  if (!spread()) {
    await closeWindow(request.cellsStateKey);
    throw new Error(`${pickedName} 줄을 골랐지만 화면이 금액을 나눠 넣지 않았습니다.`
      + `${warned.length ? ` 화면 안내: ${warned.join(' / ')}` : ''} 아무것도 바꾸지 않았습니다.`);
  }
  return {
    pending: {
      route: request.routePath,
      rowsStateKey: request.rowsStateKey,
      cellsStateKey: request.cellsStateKey,
      spec,
      identity,
      picked,
    },
    row: label,
    name: text(chosen[(spec.rowLabel ?? []).find((key) => /nm$/i.test(key)) ?? '']) || label,
    picked: pickedName,
  };
}

/**
 * 창의 확정 단추를 누른다. **Auto 일 때만** — 저장과 같은 게이트다.
 *
 * 판정은 화면으로 한다. 확정하면 화면이 창을 닫고 목록을 다시 조회한다. 그 줄의 처리 체크가
 * 켜져 있어야 처리된 것이다 — `저장: 서버에 잘 저장했습니다` 만 믿지 않는다.
 */
export async function applyWindowPick(
  pending: WindowPending,
  auto: boolean,
  timeoutMs: number,
): Promise<{ notices: string[]; saved: boolean }> {
  if (!auto) throw new Error('save 는 Auto 모드가 켜져 있을 때만 할 수 있다');
  const { spec } = pending;
  if (!gridShown(pending.cellsStateKey)) {
    return { notices: ['창이 닫혀 있습니다. 아무것도 바꾸지 않았습니다.'], saved: false };
  }
  const still = rowsOf(pending.cellsStateKey).find((row) => same(row, pending.picked));
  if (!still || text(still[spec.check]) !== '1') {
    return { notices: ['창에서 고른 줄이 풀려 있습니다. 아무것도 바꾸지 않았습니다.'], saved: false };
  }
  const button = windowButton(pending.cellsStateKey, spec.button);
  if (!button) return { notices: [`창에 ${spec.button} 단추가 없습니다. 아무것도 바꾸지 않았습니다.`], saved: false };
  await dismissNotices();
  // 창 단추는 화면 제목 영역이 덮고 있다 — 좌표로 누르면 막힌다. 요소를 직접 누른다.
  button.click();
  const notices: string[] = [];
  const deadline = Date.now() + Math.max(timeoutMs, OPEN_MS);
  // 화면이 `입금처리하시겠습니까?` 를 묻는다. Auto 가 켜진 뜻이 그것이다 — 예.
  // 묻고 나면 저장하고 `저장: 서버에 잘 저장했습니다` 를 띄운 뒤 창을 닫고 다시 조회한다.
  let asked = false;
  while (Date.now() < deadline) {
    const answer = confirmQuestion();
    if (answer) {
      asked = true;
      notices.push(answer);
      // eslint-disable-next-line no-await-in-loop
      await settleDialog(1000);
      // eslint-disable-next-line no-continue
      continue;
    }
    // eslint-disable-next-line no-await-in-loop
    notices.push(...await dismissNotices(3, 0));
    if (asked && !gridShown(pending.cellsStateKey) && !(bridge().busy?.() ?? false)) break;
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS * 4);
  }
  await untilQuiet(pending.rowsStateKey, Date.now() + timeoutMs);
  notices.push(...await dismissNotices(3, 300));
  const after = rowsOf(pending.rowsStateKey).find((row) => same(row, pending.identity));
  const refused = notices.some((one) => /필수입력확인|경고|오류|실패|취소하였/.test(one));
  const saved = !refused && !gridShown(pending.cellsStateKey) && Boolean(after) && text(after![spec.open]) === '1';
  return { notices, saved };
}
